import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null, 'success', 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    
    // Redirect to Substack with pre-filled email
    const substackUrl = `https://dyota.substack.com/subscribe?email=${encodeURIComponent(email)}`;
    window.open(substackUrl, '_blank');
    
    // Simulate success after redirect
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setEmail('');
    }, 1000);
  };

  return (
    <div className="space-y-4">
      <AnimatePresence mode="wait">
        {submitStatus === 'success' ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="text-center py-6"
          >
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            </motion.div>
            <h4 className="text-white font-semibold mb-2">Thank You!</h4>
            <p className="text-gray-400 text-sm mb-4">
              You're being redirected to complete your subscription on Substack.
            </p>
            <button
              className="text-emerald-400 hover:text-emerald-300 text-sm transition-colors"
              onClick={() => {
                setSubmitStatus(null);
                setEmail('');
              }}
            >
              Subscribe another email
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            <div className="space-y-3">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <motion.input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (submitStatus === 'error') setSubmitStatus(null);
                  }}
                  placeholder="Enter your email address"
                  className={`w-full pl-10 pr-4 py-3 bg-white/5 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all duration-300 text-sm ${
                    submitStatus === 'error' ? 'border-red-500' : 'border-white/20'
                  }`}
                  whileFocus={{ scale: 1.02 }}
                />
                <AnimatePresence>
                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute -bottom-6 left-0 flex items-center gap-1 text-red-400 text-xs"
                    >
                      <AlertCircle className="w-3 h-3" />
                      Please enter a valid email address
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-black font-semibold py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isSubmitting ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-4 h-4 border-2 border-black border-t-transparent rounded-full"
                    />
                    Redirecting...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Subscribe to Newsletter
                  </>
                )}
              </motion.button>
            </div>

            <div className="text-center">
              <p className="text-gray-400 text-xs leading-relaxed">
                Get quantum computing insights, research updates, and case studies delivered to your inbox. 
                <br />
                <a 
                  href="https://dyota.substack.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-emerald-400 hover:text-emerald-300 transition-colors inline-flex items-center gap-1 mt-1"
                >
                  Powered by Substack <ExternalLink className="w-3 h-3" />
                </a>
              </p>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NewsletterSignup;